import React from 'react';
import { planOf, planOfShop } from '../constants/plans';
import { useT } from '../i18n';

// ── WHAT A PAID TIER LOOKS LIKE ON A PROFILE HEADER ──────────────────────────
// Three marks — a badge beside the name, a tick on the avatar, a row of
// capability chips — used by BOTH profile headers: the public page a visitor
// opens (pages/DesignerProfile.jsx) and the designer's own dashboard strip
// (components/DesignerProfileHeader.jsx). A designer has to see their own
// profile the way everyone else sees it, and two copies of this markup is how
// that stops being true a month from now.
//
// The styling lives in DesignerProfileHeader.css, which both headers already
// import — see the tier block at the bottom of that file.

/**
 * The plan record to decorate a header with, or null for no decoration.
 *
 * FREE IS NOT A TIER. `searchRank > 0` rather than `id !== 'free'` because the
 * rank is already the file's own answer to "is this plan worth anything", and a
 * fifth plan added below free would be caught by it without an edit here.
 *
 * WHICH LADDER IS AN ARGUMENT, not a guess. Both roles store their tier in
 * `plan` and 'silver' means silver on either, but the tables are separate and a
 * shop has no bronze — reading the designer one for a shop would be right only
 * by coincidence. The caller already knows the role; nothing here has to sniff
 * a document shape to find out.
 */
export function tierOf(user, isShop = false) {
  const plan = isShop ? planOfShop(user) : planOf(user);
  return plan.searchRank > 0 ? plan : null;
}

// ── WHAT THE CHIPS ADVERTISE ─────────────────────────────────────────────────
// Field names from constants/plans.js, not copies of their values: the row is
// filtered by what the plan record actually says, so a flag flipped there
// changes this row and nothing has to be kept in step by hand.
//
// Listing keys rather than rendering every truthy flag is deliberate.
// `messaging` and `homepageFeature` are true on gold too, but they describe the
// account's plumbing rather than anything a visitor is choosing between
// designers on — and `topDesigner` was dropped because the badge beside the
// name already says it, in larger type, two inches away.
//
// ONE LIST PER LADDER, and each entry carries its own test rather than being
// read for truthiness. The two ladders do not agree on what their fields mean:
// a designer's `recommended` is a boolean, a shop's is one of 'none' /
// 'chance' / 'always'. A shared truthy filter would have made a silver shop
// claim "Recommended" on the strength of the string 'chance' — which is a
// chance of appearing, not a promise of it, and the chip would have been an
// overstatement the shop had not bought.
const TIER_CAPS = {
  designer: [
    ['recommended', p => p.recommended],
    ['prioritySupport', p => p.prioritySupport],
  ],
  // ONE CHIP, and only for gold. "Unlimited photos" and "On the homepage" were
  // both dropped: a visitor is choosing between shops, and neither tells them
  // anything about this shop — the first describes an allowance they cannot
  // see, and the second is about where the shop appears somewhere they are not.
  // Both were also true of gold and nothing else, so the badge already said it.
  //
  // Silver is left with no chips, exactly like bronze above: the badge, the
  // ring and the tick, claiming nothing it has not bought. `=== 'always'` and
  // not truthiness — silver's 'chance' is a chance of being recommended, not a
  // promise of it.
  shop: [
    ['recommended', p => p.recommended === 'always'],
    ['partner', p => p.partner],
  ],
};

// A crown, drawn once. It appears at three sizes across these three marks, and
// two copies of the same path is how the copies quietly drift apart.
//
// THE SAME GLYPH FOR EVERY TIER. What separates bronze from gold here is the
// metal it is drawn in, exactly as on the search and rail cards — a different
// symbol per tier would be a second thing to learn for information the colour
// already carries.
export function Crown({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M3 8.4a1.3 1.3 0 0 1 2.1-1l3.1 2.4 2.7-4.6a1.3 1.3 0 0 1 2.2 0l2.7 4.6 3.1-2.4a1.3 1.3 0 0 1 2.1 1L20.4 17a1.3 1.3 0 0 1-1.3 1.1H4.9A1.3 1.3 0 0 1 3.6 17z" />
      <rect x="4.6" y="19.3" width="14.8" height="2.1" rx="1.05" />
    </svg>
  );
}

/**
 * The tier badge that sits on the name line. Renders nothing without a tier, so
 * callers can drop it in unguarded.
 *
 * The plan's `name` is substituted rather than translated: "Gold", "Silver" and
 * "Bronze" are what the pricing page calls these plans in every language (see
 * PLANS in constants/plans.js and pl_on_plan in the i18n file), so a localised
 * word here would name a plan nobody can find.
 *
 * THE TIER ALONE, no role word after it. It used to read "Gold Designer", which
 * became wrong the moment shops started wearing this badge — and "Gold Shop" on
 * one page beside "Gold Designer" on another says the tier is two different
 * things when it is one. The header already makes the role plain.
 */
export function TierBadge({ tier }) {
  const { t } = useT();
  if (!tier) return null;
  return (
    <span className="dph-tier-badge">
      <Crown size={13} />
      {t(`pl_name_${tier.id}`)}
    </span>
  );
}

/**
 * The tick over the avatar. Belongs INSIDE the avatar button — it is positioned
 * against it, and the button has to stop clipping for the tick to overhang the
 * ring (see .dph-avatar--tier).
 *
 * Decorative on purpose: the badge beside the name already says this in words,
 * and a screen reader hearing "verified" twice learns nothing the second time.
 */
export function TierCheck({ tier }) {
  const { t } = useT();
  if (!tier) return null;
  return (
    <span
      className="dph-tier-check"
      title={t('dp_tier_verified').replace('{p}', t(`pl_name_${tier.id}`))}
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
        strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12.5l5.2 5.2L20 7" />
      </svg>
    </span>
  );
}

/**
 * The capability chips. Empty for bronze — none of the flags above are true on
 * it — and that is the honest outcome: bronze keeps the badge, the ring and the
 * tick, and claims nothing it has not bought.
 */
export function TierCaps({ tier, isShop = false }) {
  const { t } = useT();
  const caps = tier
    ? TIER_CAPS[isShop ? 'shop' : 'designer'].filter(([, test]) => test(tier)).map(([k]) => k)
    : [];
  if (caps.length === 0) return null;
  return (
    <ul className="dph-caps">
      {caps.map(k => (
        <li key={k} className="dph-cap">
          <Crown size={11} />
          {t(`dp_cap_${k}`)}
        </li>
      ))}
    </ul>
  );
}
